/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import {
  CalendarRestService,
  ConstructionScheduleRestService,
  MaintenanceScheduleRestService,
  PreConstructionScheduleRestService,
  reportField,
  reportIdField,
  SalesScheduleRestService,
  ScheduleTaskType,
  ScheduleType,
  TaskRelationType,
} from '@gh/builder-shared-data';
import {
  DictionaryService,
  getRouteParam,
  getRouteQueryParam,
  getRouteStaticData,
  InitViewService,
  isRouteForCreationMode,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { GridLayoutService } from '@gh/core-ui-grid';
import { map$, switchMap$ } from '@gh/rx/operators';
import { GanttChartLoader, GanttExtension } from '@gh/builder-shared-gantt-chart';
import { Observable } from 'rxjs';
import {
  ScheduleGanttDetails,
  scheduleGanttDetailsFromDto,
  scheduleGanttDetailsToDto,
} from './schedule-gantt-details.model';
import {
  FilterNames,
  FilterRestService,
  REPORT_CONSTRUCTION_SCHEDULE,
  ReportService,
} from '@gh/builder-shared';
import { isNil } from 'lodash';
import { throwError$ } from '@gh/rx';
import { MessageBasket } from '@gh/core-messages';
import { Confirmation } from '@gh/core-ui';

type ScheduleRestService = ConstructionScheduleRestService
  | SalesScheduleRestService
  | PreConstructionScheduleRestService
  | MaintenanceScheduleRestService;

@Injectable()
export class ScheduleGanttDetailsBusinessService implements InitViewService {
  constructor(private gridLayouts: GridLayoutService,
              private dictionaries: DictionaryService,
              private calendarRest: CalendarRestService,
              private constructionScheduleRest: ConstructionScheduleRestService,
              private salesScheduleRest: SalesScheduleRestService,
              private preConstructionScheduleRest: PreConstructionScheduleRestService,
              private maintenanceScheduleRest: MaintenanceScheduleRestService,
              private reports: ReportService,
              private filterService: FilterRestService,
              private ganttChartLoader: GanttChartLoader) {
  }
  getColumnEnableCondition = (type: ScheduleType) => ({
      ref: (): boolean => type === ScheduleType.Construction,
      status: (): boolean => type === ScheduleType.Construction,
      draw: (): boolean => type === ScheduleType.Construction,
      confirmedWith: (): boolean => type === ScheduleType.Construction,
      confirmedOnDate: (): boolean => type === ScheduleType.Construction,
      confirmedForDate: (): boolean => type === ScheduleType.Construction,
  })

  initView(route: ActivatedRouteSnapshot): any {
    const scheduleId = getRouteParam(route, 'scheduleId');
    const { type } = getRouteStaticData(route);
    const restService = this.selectRestService(type);
    const schedule$ = !scheduleId || scheduleId === 'new'
      ? restService.init(
        getRouteQueryParam(route, 'jobId') || getRouteQueryParam(route, 'salesFolderId'),
        getRouteQueryParam(route, 'templateId'),
      ).pipe(map$(scheduleGanttDetailsFromDto))
      : restService.get(scheduleId).pipe(map$(scheduleGanttDetailsFromDto));
    // call additional validation only if it is a construction schedule
    const scheduleWithValidation$ = type === ScheduleType.Construction
      ? schedule$.pipe(switchMap$((schedule) => isNil(schedule.authToCommenceDate)
        ? throwError$(MessageBasket.error('msg_no_auth_to_commence'))
        : [schedule]))
      : schedule$;
    return {
      gantt: this.ganttChartLoader.load({
        extensions: [
          GanttExtension.SmartRendering,
          GanttExtension.AutoScheduling,
          GanttExtension.Tooltip,
          GanttExtension.Marker,
          GanttExtension.ExportPdf,
        ],
      }),
      filter: this.filterService.get(this.filterName(type)),
      calendar: this.calendarRest.findAll(),
      details: scheduleWithValidation$,
      gridLayout: this.gridLayouts.get('scheduleGanttTasks', this.getColumnEnableCondition(type)),
      taskTypeDict: this.dictionaries.getByEnum(ScheduleTaskType),
      relationshipTypeDict: this.dictionaries.getByEnum(TaskRelationType),
      reportUrl: scheduleId && scheduleId !== 'new' ? type === ScheduleType.Construction && !isRouteForCreationMode(route)
        ? this.reports.generateUrl(REPORT_CONSTRUCTION_SCHEDULE, [
          reportIdField('ScheduleId', scheduleId),
          reportField('IsFromBuilderPortal', true),
        ]) : void 0 : '',
    };
  }

  get(type: ScheduleType, scheduleId: ID): Observable<ScheduleGanttDetails> {
    return this.selectRestService(type).get(scheduleId).pipe(map$(scheduleGanttDetailsFromDto));
  }

  create(type: ScheduleType, details: ScheduleGanttDetails, saveBaseDatesAnswer?: Confirmation): Observable<ScheduleGanttDetails> {
    return this.selectRestService(type).create(scheduleGanttDetailsToDto(details), saveBaseDatesAnswer).pipe(
      map$(scheduleGanttDetailsFromDto));
  }

  update(type: ScheduleType, details: ScheduleGanttDetails, saveBaseDatesAnswer?: Confirmation): Observable<ScheduleGanttDetails> {
    return this.selectRestService(type).update(scheduleGanttDetailsToDto(details), saveBaseDatesAnswer).pipe(
      map$(scheduleGanttDetailsFromDto));
  }

  filterName(type: ScheduleType | FilterNames): FilterNames {
    switch (type) {
      case ScheduleType.Construction:
        return FilterNames.ganttConstruction;
      case ScheduleType.PreConstruction:
        return FilterNames.ganttPreConstruction;
      case ScheduleType.Maintenance:
        return FilterNames.ganttMaintenance;
      case ScheduleType.Sales:
        return FilterNames.ganttSales;
      default:
        return type;
    }
  }
  saveFilter(type: ScheduleType | FilterNames, size: any): void {
    const currentFilter = this.filterService.getLocal(this.filterName(type));
    this.filterService.update(this.filterName(type), {
      ...currentFilter,
      primary: size. primary,
    }).subscribe();
  }
  private selectRestService(type: ScheduleType): ScheduleRestService {
    return {
      [ScheduleType.Construction]: this.constructionScheduleRest,
      [ScheduleType.Sales]: this.salesScheduleRest,
      [ScheduleType.PreConstruction]: this.preConstructionScheduleRest,
      [ScheduleType.Maintenance]: this.maintenanceScheduleRest,
    }[type];
  }
}
